/**
 * SendStatusOverlay — full-screen confirmation overlay shown for ~1.5s
 * after an email send attempt. Big circled green check on success, big
 * circled red X on failure. Auto-dismisses; non-interactive backdrop.
 *
 * Usage:
 *   const [status, setStatus] = useState(null);  // 'success' | 'error' | null
 *   <SendStatusOverlay status={status} onDone={() => setStatus(null)} />
 */
import React, { useEffect } from 'react';
import { Check, X } from 'lucide-react';

const SendStatusOverlay = ({ status, onDone, durationMs = 1500 }) => {
  useEffect(() => {
    if (!status) return;
    const t = setTimeout(() => onDone?.(), durationMs);
    return () => clearTimeout(t);
  }, [status, onDone, durationMs]);

  if (!status) return null;

  const isOk = status === 'success';
  const ringClass = isOk ? 'border-emerald-500' : 'border-red-500';
  const iconBg = isOk ? 'bg-emerald-500' : 'bg-red-500';
  const Icon = isOk ? Check : X;
  const label = isOk ? 'Sent' : 'Failed';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 backdrop-blur-sm pointer-events-none animate-in fade-in duration-200"
      data-testid={`send-status-overlay-${isOk ? 'success' : 'error'}`}
      aria-live="polite"
      role="status"
    >
      <div className="flex flex-col items-center gap-4">
        <div
          className={`w-32 h-32 rounded-full border-4 ${ringClass} flex items-center justify-center bg-white/95 dark:bg-[#0d0d0d]/95 shadow-2xl animate-in zoom-in-50 duration-300`}
        >
          <div className={`w-20 h-20 rounded-full ${iconBg} flex items-center justify-center`}>
            <Icon className="w-12 h-12 text-white" strokeWidth={3} />
          </div>
        </div>
        <p className={`text-lg font-semibold ${isOk ? 'text-emerald-200' : 'text-red-200'} drop-shadow-md`}>
          {label}
        </p>
      </div>
    </div>
  );
};

export default SendStatusOverlay;
